export class H264Decoder {
    onFrame;
    onError;
    decoder = null;
    configured = false;
    useAvcC = false; // true: description set → AVCC chunk format
    pendingCodec = null;
    pendingWidth;
    pendingHeight;
    // Ardışık configure başarısızlığı sayacı. 5 kez üst üste fail olursa
    // kullanıcıya fatal error gösteriyoruz (decoder hiç çalışmıyor demek).
    consecutiveConfigFails = 0;
    static MAX_CONFIG_FAILS = 5;
    constructor(width, height, onFrame, onError) {
        this.onFrame = onFrame;
        this.onError = onError;
        this.pendingWidth = width;
        this.pendingHeight = height;
    }
    async start() {
        if (!('VideoDecoder' in window)) {
            throw new Error('Bu tarayıcı WebCodecs API desteklemiyor. ' +
                'Desktop: Chrome 94+, Edge 94+, Firefox 130+. ' +
                'iPad/iPhone: iOS 17+. Android: Chrome 94+.');
        }
        // Codec support probe — gerçek configure() ilk keyframe'de SPS parse ile yapılacak.
        const codecsToTry = [
            'avc1.42E01F', 'avc1.42E01E', 'avc1.4D401F', 'avc1.4D401E', 'avc1.64001F',
        ];
        let probedCodec = null;
        for (const codec of codecsToTry) {
            try {
                const supported = await VideoDecoder.isConfigSupported({
                    codec, codedWidth: this.pendingWidth, codedHeight: this.pendingHeight,
                    optimizeForLatency: true,
                });
                if (supported.supported) {
                    probedCodec = codec;
                    break;
                }
            }
            catch { /* iOS bazen exception atıyor — ignore */ }
        }
        if (!probedCodec) {
            throw new Error('H.264 decoder hiç profile desteklemiyor (denendi: ' +
                codecsToTry.join(', ') + '). iOS 17+ veya Chrome 94+ gerekli.');
        }
        this.pendingCodec = probedCodec;
        this.recreateDecoderSilent();
    }
    /**
     * Yeni VideoDecoder instance oluştur. WebCodecs spec'e göre error
     * callback fire olduğunda state "closed" → yeniden kullanılamaz. Recovery
     * için bu metodu çağırıyoruz. onError'a ESCALATE ETMİYORUZ: iPad/mobile
     * background cycle'da decoder interrupt oluyor, sonraki IDR'de otomatik
     * toparlanıyor — kullanıcıya banner göstermek gereksiz (host-agent her 1sn
     * IDR gönderiyor).
     */
    recreateDecoderSilent() {
        this.configured = false;
        this.useAvcC = false;
        try {
            this.decoder?.close();
        }
        catch { /* zaten closed */ }
        try {
            this.decoder = new VideoDecoder({
                output: (frame) => this.onFrame(frame),
                error: (_e) => {
                    // Cascade error → yine sessizce recreate. Recursion yok: recursive
                    // error sadece decoder closed state'te decode çağrılırsa olabilir,
                    // bu da configured=false iken önleniyor (decode fonksiyonu içinde
                    // keyframe bekliyor).
                    this.recreateDecoderSilent();
                },
            });
        }
        catch (err) {
            // Tamamen crash → bu fatal, kullanıcıya bildir.
            this.onError(new Error(`decoder recreate fail: ${err}`));
        }
    }
    decode(annexB, isKeyframe, ptsUs) {
        if (!this.decoder)
            throw new Error('decoder.start() çağrılmadı');
        // Error callback decoder'ı sıfırlamışsa sonraki keyframe'e kadar drop.
        if (!this.configured) {
            if (!isKeyframe)
                return; // P-frame drop → bir sonraki IDR'i bekle
            if (!this.configureFromKeyframe(annexB))
                return; // configure başarısız
        }
        // Chunk format: description varsa AVCC (length-prefixed), yoksa Annex-B.
        let chunkData;
        if (this.useAvcC) {
            // Description'da SPS/PPS var → chunk'tan strip et, kalanı AVCC'ye çevir.
            chunkData = annexBToAvcc(annexB, /* stripParamSets */ true);
            if (chunkData.length === 0) {
                // Tamamen SPS/PPS'ten ibaret (olmaması gerekir) → skip
                return;
            }
        }
        else {
            chunkData = annexB;
        }
        const chunk = new EncodedVideoChunk({
            type: isKeyframe ? 'key' : 'delta',
            timestamp: ptsUs,
            data: chunkData,
        });
        try {
            this.decoder.decode(chunk);
        }
        catch (_e) {
            // Sync throw — closed/unconfigured decoder. Sessizce recover, kullanıcıya
            // banner göstermiyoruz. Sonraki IDR'de otomatik toparlanır (host-agent
            // 1sn aralık IDR yolluyor). Spam önlemek için onError çağırmıyoruz.
            this.recreateDecoderSilent();
        }
    }
    /** İlk keyframe'den SPS+PPS çıkar, decoder'ı configure et. */
    configureFromKeyframe(annexB) {
        if (!this.decoder)
            return false;
        const avcC = tryBuildAvcCFromAnnexB(annexB);
        // iOS Safari: description ZORUNLU (Annex-B decode etmiyor description'sız).
        // Chrome/Firefox: description yoksa Annex-B'yi kabul eder.
        const isAppleWebKit = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
            || /CriOS|FxiOS|EdgiOS/i.test(navigator.userAgent);
        if (!avcC) {
            // SPS/PPS bulamadık — iOS'ta bu çalışmaz, bekle. Non-iOS'ta fallback.
            if (isAppleWebKit) {
                // Sonraki IDR'de tekrar dene — host-agent her 1sn IDR gönderiyor.
                return false;
            }
            try {
                this.decoder.configure({
                    codec: this.pendingCodec,
                    codedWidth: this.pendingWidth,
                    codedHeight: this.pendingHeight,
                    optimizeForLatency: true,
                });
                this.configured = true;
                this.useAvcC = false;
                this.consecutiveConfigFails = 0;
                return true;
            }
            catch (_e) {
                this.onConfigureFail();
                return false;
            }
        }
        // AvcC hazır — description ile configure. Chunk'lar AVCC formatında olmalı.
        try {
            this.decoder.configure({
                codec: codecFromSps(avcC.sps) ?? this.pendingCodec,
                codedWidth: this.pendingWidth,
                codedHeight: this.pendingHeight,
                optimizeForLatency: true,
                description: avcC.bytes,
            });
            this.configured = true;
            this.useAvcC = true;
            this.consecutiveConfigFails = 0;
            return true;
        }
        catch (_e) {
            this.onConfigureFail();
            return false;
        }
    }
    /**
     * configure() başarısız oldu. N kez üst üste olursa fatal error banner,
     * değilse sessizce sonraki keyframe'i bekle (host-agent her 1sn IDR).
     */
    onConfigureFail() {
        this.consecutiveConfigFails++;
        if (this.consecutiveConfigFails >= H264Decoder.MAX_CONFIG_FAILS) {
            this.onError(new Error(`Video decoder bu cihaz/tarayıcıda çalışmıyor (${this.consecutiveConfigFails}× configure fail). ` +
                `iOS 17+ / Chrome 94+ / Edge 94+ gerekli.`));
            this.consecutiveConfigFails = 0; // spam önle
        }
    }
    /**
     * Dışarıdan zorla reset. U8.7: iPad arka plan/ön plan cycle'ında WS alive
     * kalsa bile VideoDecoder state "closed"a düşüyor (iOS JS donması). Bu
     * durumda yeni instance + bir sonraki keyframe'i bekle. session.ts bunu
     * visibilitychange=visible anında çağırıyor.
     */
    resetForResume() {
        this.recreateDecoderSilent();
        this.consecutiveConfigFails = 0;
    }
    async close() {
        if (!this.decoder)
            return;
        await this.decoder.flush().catch(() => { });
        this.decoder.close();
        this.decoder = null;
    }
}
/**
 * Annex-B byte stream'inden SPS (NAL type 7) + PPS (NAL type 8) çıkar,
 * AvcC (ISO/IEC 14496-15 §5.2.4.1.1 AVCDecoderConfigurationRecord) bytes oluştur.
 * iOS WebKit H.264 decoder için kritik.
 *
 * Layout (6-byte header — eski kodda 7 bayt yazılıyordu, off-by-one fix):
 *   [0] configurationVersion = 1
 *   [1] AVCProfileIndication   (sps[1])
 *   [2] profile_compatibility  (sps[2])
 *   [3] AVCLevelIndication     (sps[3])
 *   [4] 0xFF  (bit7..2 reserved=1, bit1..0 lengthSizeMinusOne=3 → 4-byte NAL length)
 *   [5] 0xE1  (bit7..5 reserved=1, bit4..0 numOfSPS=1)
 *   [6..7] spsLen BE
 *   [8..]  sps bytes
 *   [N]    numOfPPS = 1
 *   [N+1..N+2] ppsLen BE
 *   [N+3..]    pps bytes
 */
function tryBuildAvcCFromAnnexB(stream) {
    const nals = splitAnnexBNals(stream);
    let sps = null;
    let pps = null;
    for (const nal of nals) {
        if (nal.length === 0)
            continue;
        const nalType = nal[0] & 0x1f;
        if (nalType === 7 && !sps)
            sps = nal;
        else if (nalType === 8 && !pps)
            pps = nal;
        if (sps && pps)
            break;
    }
    if (!sps || !pps || sps.length < 4)
        return null;
    const totalLen = 6 + 2 + sps.length + 1 + 2 + pps.length;
    const out = new Uint8Array(totalLen);
    let o = 0;
    out[o++] = 0x01; // configurationVersion
    out[o++] = sps[1]; // profile
    out[o++] = sps[2]; // profile_compatibility
    out[o++] = sps[3]; // level
    out[o++] = 0xFF; // reserved + lengthSizeMinusOne=3
    out[o++] = 0xE1; // reserved + numOfSPS=1
    out[o++] = (sps.length >> 8) & 0xff;
    out[o++] = sps.length & 0xff;
    out.set(sps, o);
    o += sps.length;
    out[o++] = 0x01; // numOfPPS = 1
    out[o++] = (pps.length >> 8) & 0xff;
    out[o++] = pps.length & 0xff;
    out.set(pps, o);
    o += pps.length;
    return { bytes: out, sps, pps };
}
/**
 * Annex-B chunk'ı AVCC (ISO/IEC 14496-15) length-prefixed formata çevir.
 * 4-byte BE length + NAL unit, NAL unit başlığı (0x67 vb.) DAHİL.
 *
 * stripParamSets=true ise SPS (type 7) + PPS (type 8) atılır — description
 * zaten onları içeriyor; bazı decoder (iOS WebKit) duplicate'ten hoşlanmıyor.
 * AUD (type 9) da atılır — WebCodecs implementasyonları AUD'u beklemiyor.
 */
function annexBToAvcc(stream, stripParamSets) {
    const nals = splitAnnexBNals(stream);
    const keep = [];
    let totalLen = 0;
    for (const n of nals) {
        if (n.length === 0)
            continue;
        if (stripParamSets) {
            const t = n[0] & 0x1f;
            if (t === 7 || t === 8 || t === 9)
                continue; // SPS, PPS, AUD
        }
        keep.push(n);
        totalLen += 4 + n.length;
    }
    const out = new Uint8Array(totalLen);
    let o = 0;
    for (const n of keep) {
        const len = n.length;
        out[o++] = (len >>> 24) & 0xff;
        out[o++] = (len >>> 16) & 0xff;
        out[o++] = (len >>> 8) & 0xff;
        out[o++] = len & 0xff;
        out.set(n, o);
        o += len;
    }
    return out;
}
/** Annex-B (start-code prefixed) byte stream'i NAL unit'lerine ayır. */
function splitAnnexBNals(buf) {
    const out = [];
    let i = 0;
    while (i < buf.length) {
        // Start code bul: 0x000001 veya 0x00000001
        let scLen = 0;
        if (i + 3 < buf.length
            && buf[i] === 0x00 && buf[i + 1] === 0x00
            && buf[i + 2] === 0x00 && buf[i + 3] === 0x01) {
            scLen = 4;
        }
        else if (i + 2 < buf.length
            && buf[i] === 0x00 && buf[i + 1] === 0x00 && buf[i + 2] === 0x01) {
            scLen = 3;
        }
        else {
            i++;
            continue;
        }
        const nalStart = i + scLen;
        // Sonraki start code'u ara
        let nalEnd = buf.length;
        let j = nalStart + 1;
        while (j < buf.length - 2) {
            if (buf[j] === 0x00 && buf[j + 1] === 0x00
                && (buf[j + 2] === 0x01
                    || (j + 3 < buf.length && buf[j + 2] === 0x00 && buf[j + 3] === 0x01))) {
                nalEnd = j;
                break;
            }
            j++;
        }
        if (nalEnd > nalStart)
            out.push(buf.subarray(nalStart, nalEnd));
        i = nalEnd;
    }
    return out;
}
/** SPS bytes'tan uygun codec string üret (avc1.PPCCLL). */
function codecFromSps(sps) {
    if (sps.length < 4)
        return null;
    const profile = sps[1].toString(16).padStart(2, '0');
    const compat = sps[2].toString(16).padStart(2, '0');
    const level = sps[3].toString(16).padStart(2, '0');
    return `avc1.${profile}${compat}${level}`.toLowerCase();
}
